import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export interface Document {
  doc_id: string;
  file_name: string;
  sections_count: number;
}

export interface Section {
  id: number;
  title: string;
  content_length: number;
  page_range: number[];
  summary: string;
  text?: string;
  doc_id?: string;
  source_file?: string;
}

export interface KnowledgeGraphData {
  nodes: Array<{
    id: string;
    name: string;
    type: string;
    size: number;
    full_title?: string;
    freq?: number;
    section_id?: number;
    color?: string;
  }>;
  edges: Array<{
    source: string;
    target: string;
    relation?: string;
    weight?: number;
    color?: string;
  }>;
  is_simple?: boolean;
}

interface DataContextType {
  sections: Section[];
  setSections: (sections: Section[]) => void;
  parsedData: any;
  setParsedData: (data: any) => void;
  sessionId: string;
  setSessionId: (id: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  uploadFile: (file: File) => Promise<void>;
  uploadFiles: (files: File[]) => Promise<void>;
  loadJSON: () => Promise<void>;
  fetchKnowledgeGraph: (sectionId: number) => Promise<KnowledgeGraphData | null>;
  fetchMacroKG: () => Promise<KnowledgeGraphData | null>;
  fetchSectionMacroKG: (sectionId: number) => Promise<KnowledgeGraphData | null>;
  sendMessage: (message: string) => Promise<void>;
  submitRating: (messageIndex: number, rating: 'up' | 'down') => Promise<void>;
  submitFeedback: (messageIndex: number, feedback: string, messageContent: string) => Promise<void>;
  generateChatKG: (chatHistory: Array<{role: string, content: string}>) => Promise<KnowledgeGraphData | null>;
  chatHistory: Array<{role: string, content: string, citations?: any[]}>;
  setChatHistory: (history: Array<{role: string, content: string, citations?: any[]}>) => void;
  microKGs: { [key: string]: KnowledgeGraphData } | null;
  setMicroKGs: (kgs: { [key: string]: KnowledgeGraphData } | null) => void;
  macroKG: KnowledgeGraphData | null;
  selectedSectionIndex: number;
  setSelectedSectionIndex: (index: number) => void;
  sectionMacroKGs: { [key: number]: KnowledgeGraphData };
  setSectionMacroKGs: (kgs: { [key: number]: KnowledgeGraphData }) => void;
  setMacroKG: (kg: KnowledgeGraphData | null) => void;
  chatKG: KnowledgeGraphData | null;
  setChatKG: (kg: KnowledgeGraphData | null) => void;
  documents: Document[];
  setDocuments: (documents: Document[]) => void;
  selectedDocIds: string[];
  setSelectedDocIds: (docIds: string[]) => void;
  fetchDocuments: () => Promise<void>;
  fetchFilteredSections: (docIds: string[]) => Promise<void>;
  clearCache: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sections, setSections] = useState<Section[]>([]);
  const [parsedData, setParsedData] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('default');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<Array<{role: string, content: string, citations?: any[]}>>([]);
  const [microKGs, setMicroKGs] = useState<{ [key: string]: KnowledgeGraphData } | null>(null);
  const [macroKG, setMacroKG] = useState<KnowledgeGraphData | null>(null);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState<number>(0);
  const [sectionMacroKGs, setSectionMacroKGs] = useState<{ [key: number]: KnowledgeGraphData }>({});
  const [chatKG, setChatKG] = useState<KnowledgeGraphData | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);

  // Use the public IP directly to avoid localhost/CORS issues
  // Dynamically determine API URL based on current hostname
  const getApiBaseUrl = () => {
    const currentHost = window.location.hostname;
    const protocol = window.location.protocol;
    
    // If accessing via localhost or 127.0.0.1, try to use the public IP
    if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      // Use public IP when accessing via localhost
      return 'http://20.107.168.51:8002';
    }
    
    // If accessing via scinter.org domain, use the same domain (nginx will proxy)
    if (currentHost === 'scinter.org' || currentHost === 'www.scinter.org') {
      return `${protocol}//${currentHost}`;
    }
    
    // For other hostnames, use the same hostname with port 8002
    return `${protocol}//${currentHost}:8002`;
  };
  const API_BASE_URL = getApiBaseUrl();
  
  // Helper function to add cache-busting to URLs
  const addCacheBust = (url: string): string => {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_t=${Date.now()}`;
  };
  
  // Helper function for fetch with cache-busting headers
  const fetchWithNoCache = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const bustedUrl = addCacheBust(url);
    return fetch(bustedUrl, {
      ...options,
      headers: {
        ...options.headers,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      cache: 'no-store' as RequestCache
    });
  };
  
  // Log the API URL for debugging
  React.useEffect(() => {
    console.log('🌐 API Base URL:', API_BASE_URL);
    console.log('🖥️ Current hostname:', window.location.hostname);
    console.log('🔗 Full location:', window.location.href);
  }, [API_BASE_URL]);

  const fetchKnowledgeGraph = useCallback(async (sectionId: number): Promise<KnowledgeGraphData | null> => {
    try {
      console.log(`Fetching knowledge graph for section ${sectionId}...`);
      
      const response = await fetchWithNoCache(`${API_BASE_URL}/section/${sectionId}/kg?session_id=${sessionId}`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log(`Section ${sectionId} not found, skipping...`);
          return null;
        }
        throw new Error(`Failed to fetch knowledge graph: ${response.status}`);
      }

      const result = await response.json();
      console.log('Knowledge graph fetched:', result);
      
      // The new API returns the graph data directly, not wrapped in knowledge_graph
      return result;
    } catch (err: any) {
      console.error('Error fetching knowledge graph:', err);
      // Don't set error for 404s, just return null
      if (!err.message.includes('404')) {
        setError(err.message || 'Failed to fetch knowledge graph');
      }
      return null;
    }
  }, [sessionId, API_BASE_URL]);

  const fetchSectionMacroKG = useCallback(async (sectionId: number): Promise<KnowledgeGraphData | null> => {
    try {
      console.log(`Fetching section macro knowledge graph for section ${sectionId}...`);
      
      const response = await fetchWithNoCache(`${API_BASE_URL}/section/${sectionId}/macro-kg?session_id=${sessionId}`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log(`Section ${sectionId} not found for macro KG, skipping...`);
          return null;
        }
        throw new Error(`Failed to fetch section macro knowledge graph: ${response.status}`);
      }

      const result = await response.json();
      console.log('Section macro knowledge graph fetched:', result);
      
      // Store the result in sectionMacroKGs state
      setSectionMacroKGs(prev => ({
        ...prev,
        [sectionId]: result
      }));
      
      return result;
    } catch (err: any) {
      console.error('Error fetching section macro knowledge graph:', err);
      if (!err.message.includes('404')) {
        setError(err.message || 'Failed to fetch section macro knowledge graph');
      }
      return null;
    }
  }, [sessionId, API_BASE_URL, setSectionMacroKGs]);

  const fetchMacroKG = useCallback(async (): Promise<KnowledgeGraphData | null> => {
    try {
      console.log('Fetching macro knowledge graph...');
      console.log('Selected doc IDs:', selectedDocIds);
      
      // Build URL with doc_ids if any are selected
      let url = `${API_BASE_URL}/macro-kg?session_id=${sessionId}`;
      if (selectedDocIds.length > 0) {
        url += `&doc_ids=${selectedDocIds.join(',')}`;
      }
      
      const response = await fetchWithNoCache(url, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch macro knowledge graph: ${response.status}`);
      }

      const result = await response.json();
      console.log('Macro knowledge graph fetched:', result);
      
      // The new API returns the graph data directly, not wrapped in knowledge_graph
      return result;
    } catch (err: any) {
      console.error('Error fetching macro knowledge graph:', err);
      setError(err.message || 'Failed to fetch macro knowledge graph');
      return null;
    }
  }, [sessionId, API_BASE_URL, selectedDocIds]);

  const loadJSON = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Auto-clear cache before loading to prevent stale data
      console.log('🧹 Auto-clearing cache before loading JSON...');
      try {
        await fetchWithNoCache(`${API_BASE_URL}/clear-cache?session_id=${sessionId}`, {
          method: 'POST',
          headers: { 'Accept': 'application/json' }
        });
        console.log('✅ Cache cleared automatically');
      } catch (clearErr) {
        console.warn('⚠️ Cache clear warning (non-fatal):', clearErr);
        // Continue with load even if cache clear fails
      }

      console.log('Loading JSON data directly...');
      
      // Call the load-json endpoint
      const response = await fetchWithNoCache(`${API_BASE_URL}/load-json`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Failed to load JSON: ${response.status}`);
      }

      const result = await response.json();
      console.log('JSON loaded successfully:', result);
      setParsedData(result);
      
      // Get sections
      const sectionsResponse = await fetchWithNoCache(`${API_BASE_URL}/sections?session_id=${sessionId}`);
      if (sectionsResponse.ok) {
        const sectionsData = await sectionsResponse.json();
        setSections(sectionsData.sections);
        console.log('Sections loaded:', sectionsData.sections);
        
        // Only fetch knowledge graphs for sections that actually exist
        const kgs: { [key: string]: KnowledgeGraphData } = {};
        for (let i = 0; i < sectionsData.sections.length; i++) {
          const kg = await fetchKnowledgeGraph(i);
          if (kg) {
            kgs[sectionsData.sections[i].title] = kg;
          }
        }
        setMicroKGs(kgs);
        console.log('Knowledge graphs loaded:', kgs);

        // Fetch macro KG
        const macro = await fetchMacroKG();
        if (macro) {
          setMacroKG(macro);
          console.log('Macro KG loaded:', macro);
        }
      }
      
      setChatHistory([]); // Clear chat history on new load
    } catch (err: any) {
      console.error('Load JSON error:', err);
      setError(err.message || 'Failed to load JSON data');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, API_BASE_URL, fetchKnowledgeGraph, fetchMacroKG]);

  const uploadFile = useCallback(async (file: File) => {
    setIsLoading(true);
    setError(null);

    try {
      // Auto-clear cache before uploading to prevent stale data
      console.log('🧹 Auto-clearing cache before upload...');
      try {
        await fetchWithNoCache(`${API_BASE_URL}/clear-cache?session_id=${sessionId}`, {
          method: 'POST',
          headers: { 'Accept': 'application/json' }
        });
        console.log('✅ Cache cleared automatically');
      } catch (clearErr) {
        console.warn('⚠️ Cache clear warning (non-fatal):', clearErr);
        // Continue with upload even if cache clear fails
      }

      console.log('Uploading file:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId);
      
      const response = await fetchWithNoCache(`${API_BASE_URL}/upload-pdf`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Failed to upload file: ${response.status}`);
      }

      const result = await response.json();
      console.log('File uploaded successfully:', result);
      setParsedData(result);
      
      // Get sections after upload
      const sectionsResponse = await fetchWithNoCache(`${API_BASE_URL}/sections?session_id=${sessionId}`);
      if (sectionsResponse.ok) {
        const sectionsData = await sectionsResponse.json();
        setSections(sectionsData.sections);
        console.log('Sections loaded:', sectionsData.sections);
        
        // Fetch knowledge graphs for all sections
        const kgs: { [key: string]: KnowledgeGraphData } = {};
        for (let i = 0; i < sectionsData.sections.length; i++) {
          const kg = await fetchKnowledgeGraph(i);
          if (kg) {
            kgs[sectionsData.sections[i].title] = kg;
          }
        }
        setMicroKGs(kgs);
        console.log('Knowledge graphs loaded:', kgs);

        // Fetch macro KG
        const macro = await fetchMacroKG();
        if (macro) {
          setMacroKG(macro);
          console.log('Macro KG loaded:', macro);
        }
      }
      
      setChatHistory([]); // Clear chat history on new upload
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, API_BASE_URL, fetchKnowledgeGraph, fetchMacroKG]);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await fetchWithNoCache(`${API_BASE_URL}/documents?session_id=${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
        // Auto-select all documents by default
        if (data.documents && data.documents.length > 0 && selectedDocIds.length === 0) {
          setSelectedDocIds(data.documents.map((d: Document) => d.doc_id));
        }
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  }, [sessionId, API_BASE_URL, selectedDocIds.length]);

  const fetchFilteredSections = useCallback(async (docIds: string[]) => {
    try {
      const docIdsParam = docIds.join(',');
      const url = docIds.length > 0
        ? `${API_BASE_URL}/sections?session_id=${sessionId}&doc_ids=${docIdsParam}`
        : `${API_BASE_URL}/sections?session_id=${sessionId}`;

      const response = await fetchWithNoCache(url);
      if (response.ok) {
        const data = await response.json();
        setSections(data.sections || []);
        console.log(`Loaded ${data.sections?.length || 0} sections (filtered: ${data.filtered})`);
      }
    } catch (err) {
      console.error('Error fetching filtered sections:', err);
    }
  }, [sessionId, API_BASE_URL]);

  const clearCache = useCallback(async () => {
    try {
      console.log('🧹 Clearing cache for session:', sessionId);

      const response = await fetchWithNoCache(`${API_BASE_URL}/clear-cache?session_id=${sessionId}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to clear cache: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Cache cleared:', result);

      // Clear local state
      setSections([]);
      setParsedData(null);
      setDocuments([]);
      setSelectedDocIds([]);
      setMicroKGs(null);
      setMacroKG(null);
      setSectionMacroKGs({});
      setChatHistory([]);
      setChatKG(null);
      setError(null);

      return result;
    } catch (err: any) {
      console.error('Error clearing cache:', err);
      setError(err.message || 'Failed to clear cache');
      throw err;
    }
  }, [sessionId, API_BASE_URL]);

  const uploadFiles = useCallback(async (files: File[]) => {
    setIsLoading(true);
    setError(null);

    try {
      // Auto-clear cache before uploading to prevent stale data
      console.log('🧹 Auto-clearing cache before upload...');
      try {
        await fetchWithNoCache(`${API_BASE_URL}/clear-cache?session_id=${sessionId}`, {
          method: 'POST',
          headers: { 'Accept': 'application/json' }
        });
        console.log('✅ Cache cleared automatically');
      } catch (clearErr) {
        console.warn('⚠️ Cache clear warning (non-fatal):', clearErr);
        // Continue with upload even if cache clear fails
      }

      console.log(`Uploading ${files.length} files...`);
      
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }
      formData.append('session_id', sessionId);
      
      // Don't set Content-Type header - browser will set it automatically with boundary for FormData
      // Try multiple endpoints and methods to bypass ad blockers
      let response: Response | null = null;
      const endpoints = [
        '/api/v1/documents',  // Generic name least likely to be blocked
        '/process-documents',  // Alternative name
        '/upload-pdfs'  // Original endpoint
      ];
      
      let lastError: Error | null = null;
      
      for (const endpoint of endpoints) {
        try {
          // Try fetch first
          response = await fetchWithNoCache(`${API_BASE_URL}${endpoint}`, {
            method: 'POST',
            body: formData,
          });
          if (response.ok) break;
        } catch (fetchErr) {
          console.log(`Fetch blocked for ${endpoint}, trying XMLHttpRequest...`);
          // Fallback to XMLHttpRequest if fetch is blocked
          try {
            response = await new Promise<Response>((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('POST', `${API_BASE_URL}${endpoint}`, true);
              xhr.onload = () => {
                resolve({
                  ok: xhr.status >= 200 && xhr.status < 300,
                  status: xhr.status,
                  json: async () => JSON.parse(xhr.responseText),
                  text: async () => xhr.responseText,
                } as Response);
              };
              xhr.onerror = () => reject(new Error(`XMLHttpRequest failed for ${endpoint}`));
              xhr.send(formData);
            });
            if (response && response.ok) break;
          } catch (xhrErr) {
            lastError = xhrErr as Error;
            continue; // Try next endpoint
          }
        }
      }
      
      // Check if we got a response
      if (!response) {
        const errorMsg = lastError?.message || 'All upload methods failed. Please disable browser extensions (ad blockers) that are blocking requests to the API server (port 8002)';
        throw new Error(errorMsg);
      }

      // Check if response is ok
      if (!response.ok) {
        try {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(errorData.detail || `Failed to upload files: ${response.status}`);
        } catch (jsonErr) {
          const errorMsg = lastError?.message || 'All upload methods failed. Please disable browser extensions (ad blockers) that are blocking requests to the API server (port 8002)';
          throw new Error(errorMsg);
        }
      }

      // TypeScript now knows response is not null and ok is true
      const result = await response.json();
      console.log('Files uploaded successfully:', result);
      setParsedData(result);
      
      // Fetch documents list
      await fetchDocuments();
      
      // Get sections after upload (all documents selected by default)
      const sectionsResponse = await fetchWithNoCache(`${API_BASE_URL}/sections?session_id=${sessionId}`);
      if (sectionsResponse.ok) {
        const sectionsData = await sectionsResponse.json();
        setSections(sectionsData.sections);
        console.log('Sections loaded:', sectionsData.sections);
        
        // Fetch knowledge graphs for all sections
        const kgs: { [key: string]: KnowledgeGraphData } = {};
        for (let i = 0; i < sectionsData.sections.length; i++) {
          const kg = await fetchKnowledgeGraph(i);
          if (kg) {
            kgs[sectionsData.sections[i].title] = kg;
          }
        }
        setMicroKGs(kgs);
        console.log('Knowledge graphs loaded:', kgs);

        // Fetch macro KG
        const macro = await fetchMacroKG();
        if (macro) {
          setMacroKG(macro);
          console.log('Macro KG loaded:', macro);
        }
      }
      
      setChatHistory([]); // Clear chat history on new upload
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload files');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, API_BASE_URL, fetchKnowledgeGraph, fetchMacroKG, fetchDocuments]);

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Sending message:', message);
      console.log('Selected doc IDs:', selectedDocIds);
      
      const formData = new FormData();
      formData.append('message', message);
      formData.append('session_id', sessionId);
      // Add selected doc_ids if any are selected
      if (selectedDocIds.length > 0) {
        formData.append('doc_ids', selectedDocIds.join(','));
      }
      
      const response = await fetchWithNoCache(`${API_BASE_URL}/chat`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Failed to send message: ${response.status}`);
      }

      const result = await response.json();
      console.log('Chat response:', result);
      
      // Add user message and assistant response to chat history
      const newHistory = [
        ...chatHistory,
        { role: 'user', content: message },
        { 
          role: 'assistant', 
          content: result.response,
          citations: result.citations || []
        }
      ];
      setChatHistory(newHistory);
      
    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, API_BASE_URL, chatHistory, selectedDocIds]);

  const submitRating = useCallback(async (messageIndex: number, rating: 'up' | 'down') => {
    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('message_index', messageIndex.toString());
      formData.append('rating', rating);

      const response = await fetchWithNoCache(`${API_BASE_URL}/chat/rating`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to submit rating: ${response.status}`);
      }

      console.log('Rating submitted successfully');
    } catch (err: any) {
      console.error('Rating submission error:', err);
      setError(err.message || 'Failed to submit rating');
    }
  }, [sessionId, API_BASE_URL]);

  const submitFeedback = useCallback(async (messageIndex: number, feedback: string, messageContent: string) => {
    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('message_index', messageIndex.toString());
      formData.append('feedback', feedback);
      formData.append('message_content', messageContent);

      const response = await fetchWithNoCache(`${API_BASE_URL}/chat/feedback`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status}`);
      }

      console.log('Feedback submitted successfully');
    } catch (err: any) {
      console.error('Feedback submission error:', err);
      setError(err.message || 'Failed to submit feedback');
    }
  }, [sessionId, API_BASE_URL]);

  const generateChatKG = useCallback(async (chatHistory: Array<{role: string, content: string}>): Promise<KnowledgeGraphData | null> => {
    try {
      console.log('🚀 Generating chat-based knowledge graph...');
      console.log('📊 Chat history length:', chatHistory.length);
      console.log('🌐 API URL:', `${API_BASE_URL}/chat/generate-kg`);
      console.log('🆔 Session ID:', sessionId);
      setIsLoading(true);
      
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('chat_history', JSON.stringify(chatHistory));
      
      console.log('📤 Sending request to backend...');
      const response = await fetchWithNoCache(`${API_BASE_URL}/chat/generate-kg`, {
        method: 'POST',
        body: formData,
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend error response:', errorText);
        throw new Error(`Failed to generate chat KG: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Chat knowledge graph generated successfully:', result);
      console.log('📊 Result nodes count:', result?.nodes?.length || 0);
      console.log('🔗 Result edges count:', result?.edges?.length || 0);
      
      setChatKG(result);
      return result;
    } catch (err: any) {
      console.error('❌ Error generating chat knowledge graph:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setError(err.message || 'Failed to generate chat knowledge graph');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, API_BASE_URL]);

  const value: DataContextType = {
    sections,
    setSections,
    parsedData,
    setParsedData,
    sessionId,
    setSessionId,
    isLoading,
    setIsLoading,
    error,
    setError,
    uploadFile,
    uploadFiles,
    loadJSON,
    fetchKnowledgeGraph,
    fetchMacroKG,
    fetchSectionMacroKG,
    sendMessage,
    submitRating,
    submitFeedback,
    generateChatKG,
    chatHistory,
    setChatHistory,
    microKGs,
    setMicroKGs,
    macroKG,
    setMacroKG,
    selectedSectionIndex,
    setSelectedSectionIndex,
    sectionMacroKGs,
    setSectionMacroKGs,
    chatKG,
    setChatKG,
    documents,
    setDocuments,
    selectedDocIds,
    setSelectedDocIds,
    fetchDocuments,
    fetchFilteredSections,
    clearCache,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
